<template>
    <div class="m-profile-page">
      <div class="sticky-header">
        <FeedHeader />
      </div>
  
      <main class="m-profile-content">
        <button class="back-btn" @click="$router.back()">
          ← Назад
        </button>
  
        <div v-if="isLoadingProfile" class="links-skeleton">
          <div class="skeleton title-skeleton"></div>
          <div class="skeleton field-skeleton"></div>
          <div class="skeleton field-skeleton"></div>
          <div class="skeleton field-skeleton"></div>
          <div class="skeleton button-skeleton"></div>
        </div>
  
        <section v-else class="profile-professional">
          <h1 class="section-title">Профессиональные ссылки</h1>
  
          <div class="field" v-for="item in links" :key="item.model">
            <label class="field-label">{{ item.label }}</label>
  
            <div class="input-wrapper">
              <img
                :src="item.icon"
                :class="{ faded: !form[item.model] }"
                alt=""
              />
  
              <div class="separator"></div>
  
              <input
                type="text"
                :placeholder="`Ваш ${item.label}`"
                v-model="form[item.model]"
              />
            </div>
          </div>
  
          <button
            class="save-btn"
            :class="{ active: isChanged }"
            :disabled="!isChanged || isSaving"
            @click="handleSave"
          >
            {{ isSaving ? 'Сохраняем…' : 'Сохранить' }}
          </button>
        </section>
      </main>
  
      <transition name="fade">
        <div v-if="showSnackbar" class="snackbar">{{ snackbarMessage }}</div>
      </transition>
  
      <MobileTabBar />
    </div>
  </template>
  
  <script setup>
  import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
  import FeedHeader from '../FeedHeader.vue';
  import MobileTabBar from '../MobileTabBar2.vue';
  import UserService from '../../api/UserService';
  import SummaryService from '../../api/SummaryService';
  
  import githubIcon from '../../assets/github-profile.png';
  import dribbbleIcon from '../../assets/dribbble-profile.png';
  import behanceIcon from '../../assets/behance-profile.png';
  
  const userId = localStorage.getItem('user_id');
  
  const user = ref({});
  const isLoadingProfile = ref(true);
  const isSaving = ref(false);
  const showSnackbar = ref(false);
  const snackbarMessage = ref('');
  const snackbarType = ref('success');
  const initialSnapshot = ref('');
  let snackbarTimer = null;
  
  const form = ref({
    github: '',
    dribbble: '',
    behance: '',
  });
  
  const links = [
    { label: 'Github', model: 'github', icon: githubIcon },
    { label: 'Dribbble', model: 'dribbble', icon: dribbbleIcon },
    { label: 'Behance', model: 'behance', icon: behanceIcon },
  ];
  
  const isChanged = computed(() => {
    return JSON.stringify(form.value) !== initialSnapshot.value;
  });
  
  function showSnackbarMessage(message, type = 'success') {
    snackbarMessage.value = message;
    snackbarType.value = type;
    showSnackbar.value = true;
  
    clearTimeout(snackbarTimer);
    snackbarTimer = setTimeout(() => {
      showSnackbar.value = false;
    }, 3000);
  }
  
  function getLocalProfile() {
    try {
      return JSON.parse(localStorage.getItem('user_profile')) || {};
    } catch {
      return {};
    }
  }
  
  function syncLinksFromProfile(profile) {
    const workingProfiles = profile?.working_profiles || {};
  
    form.value = {
      github: workingProfiles.githubID || '',
      dribbble: workingProfiles.dribbbleID || '',
      behance: workingProfiles.behanceID || '',
    };
  
    initialSnapshot.value = JSON.stringify(form.value);
  }
  
  function setUser(profile) {
    user.value = profile || {};
    syncLinksFromProfile(user.value);
    localStorage.setItem('user_profile', JSON.stringify(user.value));
  }
  
  function updateUserCacheLinks() {
    user.value = {
      ...user.value,
      working_profiles: {
        ...(user.value?.working_profiles || {}),
        githubID: form.value.github,
        githubEnabled: !!form.value.github,
        dribbbleID: form.value.dribbble,
        dribbbleEnabled: !!form.value.dribbble,
        behanceID: form.value.behance,
        behanceEnabled: !!form.value.behance,
      },
    };
  
    localStorage.setItem('user_profile', JSON.stringify(user.value));
  }
  
  async function loadProfile() {
    const startedAt = performance.now();
    isLoadingProfile.value = true;
  
    const cached = getLocalProfile();
  
    if (cached && Object.keys(cached).length) {
      user.value = cached;
      syncLinksFromProfile(cached);
    }
  
    try {
      const fetched = await UserService.getProfile(userId);
      setUser(fetched || {});
    } catch (error) {
      console.error('Ошибка при загрузке профессиональных ссылок:', error);
      showSnackbarMessage('Не удалось загрузить ссылки', 'error');
    } finally {
      const minSkeletonMs = 400;
      const elapsed = performance.now() - startedAt;
      const delay = Math.max(0, minSkeletonMs - elapsed);
  
      setTimeout(() => {
        isLoadingProfile.value = false;
      }, delay);
    }
  }
  
  async function handleSave() {
    if (isSaving.value || !isChanged.value) return;
  
    if (!userId) {
      showSnackbarMessage('Пользователь не авторизован', 'error');
      return;
    }
  
    isSaving.value = true;
  
    const payload = {
      githubID: form.value.github,
      githubEnabled: !!form.value.github,
      dribbbleID: form.value.dribbble,
      dribbbleEnabled: !!form.value.dribbble,
      behanceID: form.value.behance,
      behanceEnabled: !!form.value.behance,
    };
  
    try {
      const success = await SummaryService.updateWorkingProfiles(userId, payload);
  
      if (!success) {
        showSnackbarMessage('Не удалось сохранить ссылки 😢', 'error');
        return;
      }
  
      updateUserCacheLinks();
      initialSnapshot.value = JSON.stringify(form.value);
      showSnackbarMessage('Профессиональные ссылки обновлены ✅');
    } catch (error) {
      console.error('Ошибка при сохранении профессиональных ссылок:', error);
      showSnackbarMessage('Ошибка при сохранении 😢', 'error');
    } finally {
      isSaving.value = false;
    }
  }
  
  onMounted(() => {
    loadProfile();
  });
  
  onBeforeUnmount(() => {
    clearTimeout(snackbarTimer);
  });
  </script>
  
  <style scoped>
  .m-profile-page {
    width: 100vw;
    height: 100vh;
    height: 100dvh;
    background: var(--background-color);
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }
  
  .sticky-header {
    flex-shrink: 0;
  }
  
  .m-profile-content {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    padding: 16px;
    padding-bottom: 96px;
    box-sizing: border-box;
    -webkit-overflow-scrolling: touch;
  }
  
  .back-btn {
    height: 36px;
    padding: 0 14px;
    margin-bottom: 14px;
    border: none;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.92);
    backdrop-filter: blur(8px);
    color: #111;
    font-size: 13px;
    font-family: 'Eastman-Medium', sans-serif;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.12);
  }
  
  [data-theme='dark'] .back-btn {
    background: rgba(40, 40, 40, 0.92);
    color: #fff;
  }
  
  .profile-professional {
    width: 100%;
    max-width: 430px;
    margin: 0 auto;
    padding: 20px;
    box-sizing: border-box;
    border-radius: 24px;
    background: var(--sidebar-background);
    border: 1px solid #e6e6e6;
    font-family: 'Eastman-Regular', sans-serif;
  }
  
  [data-theme='dark'] .profile-professional {
    background: #2f2f2f;
    border-color: #3f3f3f;
    color: #eee;
  }
  
  .section-title {
    margin: 0 0 18px;
    font-size: 18px;
    line-height: 1.2;
    font-family: 'Eastman-Bold', sans-serif;
    color: var(--text-primary);
    letter-spacing: -0.4px;
  }
  
  .field {
    display: flex;
    flex-direction: column;
    margin-bottom: 16px;
  }
  
  .field-label {
    margin-bottom: 8px;
    font-size: 12px;
    font-family: 'Eastman-Medium', sans-serif;
    color: var(--text-primary);
  }
  
  .input-wrapper {
    min-height: 50px;
    display: flex;
    align-items: center;
    box-sizing: border-box;
    border: 1px solid #ddd;
    border-radius: 16px;
    padding: 0 14px;
    background: var(--input-background);
  }
  
  [data-theme='dark'] .input-wrapper {
    background: #3a3a3a;
    border-color: #555;
  }
  
  .input-wrapper img {
    width: 20px;
    height: 20px;
    transition: opacity 0.2s ease;
  }
  
  .input-wrapper img.faded {
    opacity: 0.3;
  }
  
  [data-theme='dark'] .input-wrapper img {
    filter: brightness(0.8) contrast(1.2);
  }
  
  .separator {
    width: 1px;
    height: 24px;
    background: #ddd;
    margin: 0 12px;
  }
  
  [data-theme='dark'] .separator {
    background: #666;
  }
  
  .input-wrapper input {
    min-width: 0;
    flex: 1;
    border: none;
    background: transparent;
    outline: none;
    font-size: 13px;
    color: var(--text-primary);
    font-family: 'Eastman-Regular', sans-serif;
  }
  
  .input-wrapper input::placeholder {
    color: var(--text-secondary);
  }
  
  .save-btn {
    width: 100%;
    height: 50px;
    margin-top: 22px;
    border: none;
    border-radius: 999px;
    background: #2173e5;
    color: white;
    font-size: 15px;
    font-family: 'Eastman-Medium', sans-serif;
    cursor: pointer;
    opacity: 0.45;
    pointer-events: none;
    box-shadow: none;
    transition: all 0.2s ease;
  }
  
  .save-btn.active {
    opacity: 1;
    pointer-events: auto;
    box-shadow: 0 8px 24px rgba(33, 115, 229, 0.24);
  }
  
  .save-btn:disabled {
    cursor: not-allowed;
  }
  
  .snackbar {
    position: fixed;
    left: 50%;
    bottom: calc(86px + env(safe-area-inset-bottom));
    transform: translateX(-50%);
    max-width: calc(100vw - 32px);
    background: #2173e5;
    color: white;
    padding: 10px 18px;
    border-radius: 14px;
    font-family: 'Eastman-Medium', sans-serif;
    font-size: 13px;
    box-shadow: 0 4px 18px rgba(0, 0, 0, 0.22);
    z-index: 110;
  }
  
  .links-skeleton {
    width: 100%;
    max-width: 430px;
    margin: 0 auto;
    padding: 20px;
    box-sizing: border-box;
    border-radius: 24px;
    background: var(--sidebar-background);
  }
  
  .skeleton {
    background: linear-gradient(90deg, #e0e0e0 25%, #f0f0f0 50%, #e0e0e0 75%);
    background-size: 200% 100%;
    animation: skeleton-shimmer 1.2s infinite;
    border-radius: 14px;
  }
  
  [data-theme='dark'] .skeleton {
    background: linear-gradient(90deg, #333 25%, #444 50%, #333 75%);
    background-size: 200% 100%;
  }
  
  .title-skeleton {
    width: 76%;
    height: 24px;
  }
  
  .field-skeleton {
    width: 100%;
    height: 50px;
    margin-top: 16px;
  }
  
  .button-skeleton {
    width: 100%;
    height: 50px;
    margin-top: 22px;
    border-radius: 999px;
  }
  
  .fade-enter-active,
  .fade-leave-active {
    transition: all 0.25s ease;
  }
  
  .fade-enter-from,
  .fade-leave-to {
    opacity: 0;
    transform: translateY(8px);
  }
  
  @keyframes skeleton-shimmer {
    0% {
      background-position: -200% 0;
    }
    100% {
      background-position: 200% 0;
    }
  }
  
  @media (min-width: 768px) {
    .m-profile-page {
      max-width: 430px;
      margin: 0 auto;
      border-left: 1px solid var(--separator_home);
      border-right: 1px solid var(--separator_home);
    }
  }
  
  @media (max-width: 360px) {
    .m-profile-content {
      padding-left: 12px;
      padding-right: 12px;
    }
  
    .profile-professional,
    .links-skeleton {
      padding: 16px;
    }
  
    .section-title {
      font-size: 17px;
    }
  }
  </style>
  